import { Box, Button, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import SingleDatePicker from "../../../components/form/SingleDatePicker";
import React, { useEffect, useRef, useState } from "react";
import ProfileCard from "../../common/ProfileCard";
import ItemDetailsTable from "../../common/ItemDetailsTable";
import pricingEngineForDocuments from "../../../utils/pricingEngineForDocuments";
import dayjs from "dayjs";
import TopBar from "../../../components/TopBar";
import PaymentMeansFields from "../../../components/form/PaymentMeansFields";
import { map } from "lodash";
import { toReactHookFormErrorPath } from "../../../utils/apiErrorPaths";
import {
  toApiAdditionalItemIdentification,
  toFormAdditionalItemIdentification,
} from "../../../utils/additionalItemIdentification";

import DocumentCurrencyInput from "../../../components/form/DocumentCurrencyInput";
import CustomTextInput from "../../../components/CustomTextInput";
import DocumentSummarySection from "../../../components/DocumentSummarySection";
import CopyTextComponent from "../../../components/CopyTextComponent";
import { useSelector } from "react-redux";
import {
  DEFAULT_CURRENCY_CODE,
  DEFAULT_DOCUMENT_SOURCE,
  DEFAULT_PURCHASE_TRANSACTION_TYPE_CODE,
  DEFAULT_SALES_TRANSACTION_TYPE_CODE,
  OMN_COUNTRY_CODE,
  PURCHASE_INVOICE_DOCUMENT_TYPE_CODE,
  SALES_INVOICE_DOCUMENT_TYPE_CODE,
} from "../../../constants/defaults";
import TransactionTypeFields from "../../../components/form/TransactionTypeFields";
import { useAutoPopulateForm } from "../../../hooks/useAutoPopulateForm";
import { useNavigate } from "react-router-dom";
import DocumentLevelChargeAndAllowancesButton from "../../common/DocumentLevelChargeAndAllowancesButton";
import DocumentDetailedPeppolStatus from "../../common/DocumentDetailedPeppolStatus";
import DocumentTimeline from "../../common/DocumentTimeline";
import BusinessProfileSelect from "../../../components/form/BusinessProfileSelect";
import CustomerSelect from "../../../components/form/CustomerSelect";
import AddLineItemButton from "../../../components/form/AddLineItemButton";
import { yupResolver } from "@hookform/resolvers/yup";
import DocumentNoteField from "../../common/DocumentNoteField";
import InvoiceValidation from "../../../validations/InvoiceValidation";
import AddPrepaymentDetailsButton from "../../common/AddPrepaymentDetailsButton";
import SupplierProfileSelect from "../../../components/form/SupplierProfileSelect";
import {
  Refresh as RefreshIcon,
  Timeline as TimelineIcon,
} from "@mui/icons-material";
import { isSimplifiedTransactionType } from "../../common/isSimplifiedTransactionType";
import { isPrepaymentTransactionType } from "../../common/isPrepaymentTransactionType";
import {
  getCountryOptionByCode,
  getSubdivisionOptionByCode,
} from "../../../api/codelist/codeListsSlice";
import { transactionTypePositionCheck } from "../../../utils/helper";
import { isProfitMarginTransactionType } from "../../common/isProfitMarginTransactionType";
import AddPrecedingInvoiceDetailsButton from "../../common/AddPrecedingInvoiceDetailsButton";
import {toApiAdditionalItemProperties, toFormAdditionalItemProperties} from "../../../utils/additionalItemProperty";

export default function InvoiceForm({
  initialData = {},
  onSubmit,
  onCancel,
  readOnly = false,
  apiErrors = {},
  isLoading = false,
  isPurchase = false,
  isResubmit = false,
}) {
  const { settings } = useSelector((state) => state.org.org);
  const navigate = useNavigate();
  const [profilesData, setProfilesData] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [supplierData, setSupplierData] = useState(null);
  const [isTimelineOpen, setIsTimelineOpen] = useState(false);
  const backAction = () => {
    if (readOnly) {
      navigate(-1);
    } else {
      onCancel();
    }
  };
  const handleTimelineOpen = () => {
    setIsTimelineOpen(true);
  };
  const handleTimelineClose = () => setIsTimelineOpen(false);

  const countryOption = useSelector(
    getCountryOptionByCode(
      initialData.delivery?.delivery_location?.address?.country_code,
    ),
  );
  const subdivisionOption = useSelector(
    getSubdivisionOptionByCode(
      initialData.delivery?.delivery_location?.address?.country_subentity,
    ),
  );

  const {
    control,
    getValues,
    setValue,
    handleSubmit,
    setError,
    formState: { errors },
    trigger,
  } = useForm({
    resolver: yupResolver(InvoiceValidation),
    context: {
      auto_numbering_enabled: settings?.auto_numbering_enabled ?? false,
    },
    mode: "onChange",
    defaultValues: {
      invoice_type_code:
        initialData?.invoice_type_code ||
        (isPurchase
          ? PURCHASE_INVOICE_DOCUMENT_TYPE_CODE
          : SALES_INVOICE_DOCUMENT_TYPE_CODE),
      document_source: DEFAULT_DOCUMENT_SOURCE,

      transaction_type:
        initialData?.transaction_type ||
        (isPurchase
          ? DEFAULT_PURCHASE_TRANSACTION_TYPE_CODE
          : DEFAULT_SALES_TRANSACTION_TYPE_CODE),

      document_number: initialData?.document_number || "",
      issue_date: dayjs(initialData?.issue_date) || null,
      due_date: dayjs(initialData?.due_date) || null,

      issue_time: new Date().toISOString().slice(11, 19) + "Z",

      payment_means: initialData?.payment_means || {
        payment_means_code: "", // Default to "Instrument not defined"
        payment_id: [],
        card_account: {
          primary_account_number_id: null,
          network_id: null,
        },
        payee_financial_account: { id: null },
        payment_mandate: { id: null, payer_financial_account_id: null },
      },
      document_lines: (initialData?.document_lines ?? []).map(
        (item, index) => ({
          ...item,
          additional_item_identification: toFormAdditionalItemIdentification(
            item?.additional_item_identification,
          ),
          additional_item_property: toFormAdditionalItemProperties(
              item?.additional_item_property,
          ),
          key: item?.key || `ITM-${index}`,
        }),
      ),
      prepaid_amount: initialData?.prepaid_amount || null,
      document_currency_code: initialData?.document_currency_code || "OMR",
      tax_exchange_rate: initialData?.tax_exchange_rate || null,
      accounting_supplier_party: initialData?.accounting_supplier_party || null,
      accounting_customer_party: initialData?.accounting_customer_party || null,
      charges: initialData?.charges || [],
      allowances: initialData?.allowances || [],

      invoice_period_start_date: initialData?.invoice_period?.start_date
        ? dayjs(initialData.invoice_period.start_date)
        : null,
      invoice_period_end_date: initialData?.invoice_period?.end_date
        ? dayjs(initialData.invoice_period.end_date)
        : null,
      invoice_period_description:
        initialData?.invoice_period?.description || null,
      delivery_address_line:
        initialData?.delivery?.delivery_location?.address?.address_line || null,
      delivery_additional_street_name:
        initialData?.delivery?.delivery_location?.address
          ?.additional_street_name || null,
      delivery_party_name: initialData?.delivery?.party_name || null,
      delivery_incoterms: initialData?.delivery?.terms || null,
      delivery_party_id: initialData?.delivery?.party_id || null,
      delivery_date: initialData?.delivery?.actual_delivery_date
        ? dayjs(initialData?.delivery?.actual_delivery_date)
        : null,
      delivery_street_name:
        initialData?.delivery?.delivery_location?.address?.street_name || null,
      delivery_city_name:
        initialData?.delivery?.delivery_location?.address?.city_name || null,
      delivery_country_code: initialData?.delivery?.delivery_location?.address
        ?.country_code
        ? countryOption
        : null,
      delivery_country_subentity:
        countryOption?.code === OMN_COUNTRY_CODE
          ? subdivisionOption
          : initialData.delivery?.delivery_location?.address?.country_subentity,

      delivery_postal_zone:
        initialData?.delivery?.delivery_location?.address?.postal_zone || null,
      prepayment_invoice_reference:
        initialData?.prepayment_invoice_reference || null,
      billing_reference: initialData?.billing_reference || [],
      note: initialData?.note || "",
    },
  });

  const addPrepaidAmount = (prepaidAmount) => {
    setValue("prepaid_amount", prepaidAmount, { shouldValidate: true });
  };

  const addPrepaymentDetails = (details) => {
    setValue("prepayment_invoice_reference", details, { shouldValidate: true });
  };

  const addPrecedingInvoiceDetails = (details) => {
    setValue("billing_reference", details, { shouldValidate: true });
  };

  useEffect(() => {
    map(apiErrors, (value, key) => {
      setError(toReactHookFormErrorPath(key), {
        message: value,
        type: "string",
      });
    });
  }, [setError, apiErrors]);

  const {
    fields: lineItems,
    replace: replaceLineItems,
    append: appendLineItem,
    remove: removeLineItem,
  } = useFieldArray({
    control,
    name: "document_lines",
  });

  const handleSetBilledTo = (data) => {
    setValue("accounting_customer_party", data);
  };

  useAutoPopulateForm({
    profiles: profilesData,
    customers: customerData,
    suppliers: supplierData,
    documentType: isPurchase ? "PURCHASE" : "SALES",
    setValue,
    getValues,
  });

  const billedBy = useWatch({ control, name: "accounting_supplier_party" });
  const billedTo = useWatch({ control, name: "accounting_customer_party" });
  const document_lines = useWatch({ control, name: "document_lines" }) || [];
  const docCharges = useWatch({ control, name: "charges" }) || [];
  const docAllowances = useWatch({ control, name: "allowances" }) || [];
  const transactionType = useWatch({ control, name: "transaction_type" });
  const invoiceType = isSimplifiedTransactionType(transactionType)
    ? "SIMPLIFIED"
    : "FULL";
  const isPrepayment = isPrepaymentTransactionType(transactionType);
  const isProfitMargin = isProfitMarginTransactionType(transactionType);
  const wasPrepayment = useRef(isPrepayment);
  const wasProfitMargin = useRef(isProfitMargin);
  const prepaidAmount = useWatch({ control, name: "prepaid_amount" });
  const watchedPaymentMeans = useWatch({
    control,
    name: "payment_means",
  }) || {
    payment_means_code: "30",
    payment_id: ["PAY-001"],
    payee_financial_account: {
      id: "AE070331234567890123456",
      name: "Supplier Company LLC",
      financial_institution_branch_id: "BOMLAEAD",
    },
  };
  const conversionRate = useWatch({
    control,
    name: "tax_exchange_rate.calculation_rate",
  });

  const documentDate = useWatch({ control, name: "issue_date" });
  useEffect(() => {
    // Re-trigger due_date validation when document_date changes
    trigger("due_date");
  }, [documentDate, trigger]);

  useEffect(() => {
    if (!readOnly && wasPrepayment.current && !isPrepayment) {
      setValue("prepaid_amount", null);
      setValue("prepayment_invoice_reference", null);
    }
    wasPrepayment.current = isPrepayment;
  }, [isPrepayment, readOnly, setValue]);

  useEffect(() => {
    if (!readOnly && wasProfitMargin.current && !isProfitMargin) {
      setValue("billing_reference[0]", null);
    }
    wasProfitMargin.current = isProfitMargin;
  }, [isProfitMargin, readOnly, setValue]);

  const handleFormSubmit = async (data) => {
    // Transform document_lines to ensure proper price structure
    const transformedDocumentLines = data.document_lines?.map((line) => ({
      ...line,
      additional_item_identification: toApiAdditionalItemIdentification(
        line.additional_item_identification,
      ),
      additional_item_property: toApiAdditionalItemProperties(
          line.additional_item_property
      ),
      price: {
        base_amount: line.price?.base_amount || 1,
        base_quantity: line.price?.base_quantity || 1,
        allowance: {
          amount: line.price?.allowance?.amount || 0,
          base_amount:
            line.price?.base_amount || line.price?.allowance?.base_amount || 1,
          reason_code: line.price?.allowance?.reason_code || null,
          reason: line.price?.allowance?.reason || null,
          tax_category: line.price?.allowance?.tax_category || null,
        },
      },
    }));

    const {
      invoice_period_start_date,
      invoice_period_end_date,
      invoice_period_description,
      ...restData
    } = data;

    const hasInvoicePeriod =
      invoice_period_start_date ||
      invoice_period_end_date ||
      invoice_period_description;

    const shouldSendDeliveryData =
      transactionTypePositionCheck(data.transaction_type, 7) ||
      transactionTypePositionCheck(data.transaction_type, 13) ||
      transactionTypePositionCheck(data.transaction_type, 12);

    const payload = {
      ...restData,
      accounting_supplier_party: {
        ...data.accounting_supplier_party,
        uuid: data.accounting_supplier_party.id,
      },
      accounting_customer_party: {
        ...data.accounting_customer_party,
        uuid: data.accounting_customer_party.id,
      },
      document_lines: transformedDocumentLines,
      due_date: dayjs(data.due_date).format("YYYY-MM-DD"),
      issue_date: dayjs(data.issue_date).format("YYYY-MM-DD"),
      issue_time: dayjs().format("HH:mm:ss"),
      ...(shouldSendDeliveryData && {
        delivery: {
          party_name: data?.delivery_party_name,
          actual_delivery_date: data?.delivery_date
            ? dayjs(data.delivery_date).format("YYYY-MM-DD")
            : null,
          terms: data?.delivery_incoterms,
          party_id: data?.delivery_party_id,
          delivery_location: {
            address: {
              additional_street_name: data?.delivery_additional_street_name,
              address_line: data?.delivery_address_line,
              street_name: data?.delivery_street_name,
              city_name: data?.delivery_city_name,
              country_code: data?.delivery_country_code?.code,
              country: data?.delivery_country_code?.label,
              country_subentity:
                data?.delivery_country_code?.code === OMN_COUNTRY_CODE
                  ? data?.delivery_country_subentity?.code
                  : data?.delivery_country_subentity,
              postal_zone: data?.delivery_postal_zone,
            },
          },
        },
      }),
      invoice_period: hasInvoicePeriod
        ? {
            start_date: invoice_period_start_date
              ? dayjs(invoice_period_start_date).format("YYYY-MM-DD")
              : null,
            end_date: invoice_period_end_date
              ? dayjs(invoice_period_end_date).format("YYYY-MM-DD")
              : null,
            description: invoice_period_description || null,
          }
        : null,
      sub_total: subtotal,
      total_excluding_tax: totalExcludingTax,
      total_tax: totalTaxAmount,
      total_including_tax: totalIncludingTax,
      total_payable: totalPayableAmount,
      header_allowance: headerAllowance,
      header_charges: headerCharges,
      payment_means: null,

      total_paid: 0,
      tax_currency_code:
        data.document_currency_code === "OMR" ? null : DEFAULT_CURRENCY_CODE,
    };

    onSubmit(payload);
  };

  const {
    itemsSubtotal,
    subtotal,
    totalTaxAmount,
    taxInOMR,
    totalItemAllowances,
    totalExcludingTax,
    totalIncludingTax,
    totalItemCharges,
    totalPayableAmount,
    totalTaxableAmount,
    totalNonTaxableAmount,
    taxBreakdown,
    headerAllowance,
    headerCharges,
    payableRoundingAmount,
  } = pricingEngineForDocuments({
    items: document_lines,
    docCharges: docCharges,
    docAllowances: docAllowances,
    conversionRatePerOMR: conversionRate,
    prepaidAmount: prepaidAmount,
    ...(readOnly &&
      initialData?.payable_rounding_amount != null && {
        payableRoundingAmount: initialData.payable_rounding_amount,
      }),
  });

  const getAllErrorMessages = (error) => {
    if (!error || typeof error !== "object") return [];

    return Object.entries(error).flatMap(([key, value]) => {
      if (key === "message" && typeof value === "string") {
        return [value];
      }

      return getAllErrorMessages(value);
    });
  };

  const getSellersErrorMessage = (error) => {
    if (!error?.accounting_supplier_party) return null;

    const messages = getAllErrorMessages(error.accounting_supplier_party);

    return messages.length ? messages.join(", ") : null;
  };

  const getBuyersErrorMessage = (error) => {
    if (!error?.accounting_customer_party) return null;

    const messages = getAllErrorMessages(error.accounting_customer_party);

    return messages.length ? messages.join(", ") : null;
  };

  const sellersErrorMessage = getSellersErrorMessage(errors);
  const buyersErrorMessage = getBuyersErrorMessage(errors);

  return (
    <>
      <Box className="InvoiceDetails" sx={{ pt: 2, pb: 4 }}>
        <Grid
          container
          sx={{
            display: "block",
          }}
        >
          <Grid>
            <TopBar
              title={
                <Stack direction="row" gap={3}>
                  {readOnly ? (
                    <>
                      {"Invoice Details |"}{" "}
                      <CopyTextComponent copyText={initialData.id} />{" "}
                    </>
                  ) : isPurchase ? (
                    "Self Billed Invoice Form"
                  ) : (
                    "Invoice Form"
                  )}
                </Stack>
              }
            >
              <Stack direction="row" spacing={1}>
                {readOnly && (
                  <>
                    <Button
                      data-testid="refresh-button"
                      variant="outlined"
                      color="primary"
                      onClick={() => window.location.reload()}
                    >
                      <RefreshIcon
                        fontSize="small"
                        color="primary"
                        sx={{ mr: 1 }}
                      />
                      Refresh
                    </Button>
                    {initialData && (
                      <Button
                        data-testid="timeline-button"
                        variant="outlined"
                        color="primary"
                        onClick={handleTimelineOpen}
                      >
                        <TimelineIcon
                          fontSize="small"
                          color="primary"
                          sx={{ mr: 1 }}
                        />
                        {"Timeline"}
                      </Button>
                    )}
                  </>
                )}
                <Button
                  data-testid="cancel-button"
                  variant={readOnly ? "contained" : "outlined"}
                  color="primary"
                  onClick={backAction}
                  disabled={isLoading}
                >
                  {readOnly ? "Back" : "Cancel"}
                </Button>

                {!readOnly && (
                  <Button
                    data-testid="submit-button"
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit(handleFormSubmit)}
                    disabled={isLoading}
                  >
                    {"Submit"}
                  </Button>
                )}
              </Stack>
            </TopBar>
          </Grid>
        </Grid>
      </Box>

      {readOnly && initialData?.id && (
        <DocumentDetailedPeppolStatus
          documentType="invoices"
          docId={initialData.id}
          metaInfo={initialData?.meta_info}
          isPurchase={isPurchase}
          buyer={initialData?.accounting_customer_party}
          documentSource={initialData?.document_source}
        />
      )}

      {readOnly && initialData?.id && (
        <DocumentTimeline
          documentType="invoices"
          docId={initialData.id}
          isPurchase={isPurchase}
          isOpen={isTimelineOpen}
          onClose={handleTimelineClose}
        />
      )}

      <Box className="InvoiceDetails">
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <CustomTextInput
              data-testid="document-number-input"
              label="Invoice Number"
              disabled={settings?.auto_numbering_enabled}
              control={control}
              helperText={
                settings?.auto_numbering_enabled
                  ? "Document number will be generated automatically"
                  : ""
              }
              errors={errors}
              name={"document_number"}
              readOnly={readOnly || isResubmit}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <SingleDatePicker
              data-testid="invoice-date-input"
              name="issue_date"
              label="Invoice Issue Date"
              control={control}
              errors={errors}
              readOnly={readOnly}
              maxDate={dayjs()}
            />
          </Grid>{" "}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <SingleDatePicker
              data-testid="invoice-due-date-input"
              name="due_date"
              label="Due Date"
              control={control}
              errors={errors}
              readOnly={readOnly}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} mt={2}>
          <PaymentMeansFields
            control={control}
            errors={errors}
            readOnly={readOnly}
            watchedPaymentMeans={watchedPaymentMeans}
            setValue={setValue}
          />

          <TransactionTypeFields
            control={control}
            errors={errors}
            readOnly={readOnly}
            profileExecutionId={transactionType}
            setValue={setValue}
          />
        </Grid>

        <Grid container spacing={2} sx={{ mt: 3 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            {isPurchase ? (
              <SupplierProfileSelect
                data-testid="billed-by-select"
                label="Billed by"
                name="accounting_supplier_party"
                control={control}
                errors={sellersErrorMessage}
                readOnly={readOnly}
                onSupplierLoaded={setSupplierData}
              />
            ) : (
              <BusinessProfileSelect
                data-testid="billed-by-select"
                label="Billed by"
                name="accounting_supplier_party"
                control={control}
                errors={sellersErrorMessage}
                readOnly={readOnly}
                onProfilesLoaded={setProfilesData}
              />
            )}
            {billedBy && (
              <Box sx={{ mt: 2, fontSize: "0.875rem", color: "black" }}>
                <ProfileCard
                  title="Billed By"
                  subtitle="Seller Info"
                  document={{
                    ...billedBy,
                  }}
                />
              </Box>
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            {isPurchase ? (
              <BusinessProfileSelect
                data-testid="billed-to-select"
                label="Billed to"
                name="accounting_customer_party"
                control={control}
                errors={buyersErrorMessage}
                readOnly={readOnly}
                onProfilesLoaded={setProfilesData}
              />
            ) : (
              <CustomerSelect
                data-testid="billed-to-select"
                label="Billed to"
                name="accounting_customer_party"
                control={control}
                errors={buyersErrorMessage}
                readOnly={readOnly}
                onSelect={handleSetBilledTo}
                setError={setError}
                onCustomerLoaded={setCustomerData}
                invoiceType={invoiceType}
              />
            )}
            {billedTo && (
              <Box sx={{ mt: 2, fontSize: "0.875rem", color: "black" }}>
                <ProfileCard
                  title="Billed To"
                  subtitle="Buyer Info"
                  rawData={billedTo}
                  readOnly={readOnly}
                  onUpdate={handleSetBilledTo}
                  document={{
                    ...billedTo, // Use the selected value from the form state
                  }}
                  invoiceType={invoiceType}
                />
              </Box>
            )}
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid size={{ xs: 12 }}>
            <Grid
              container
              sx={{ display: "flex", justifyContent: "space-between" }}
            >
              <Grid>
                {!readOnly && (
                  <AddLineItemButton
                    errors={errors}
                    addItem={appendLineItem}
                    lineItems={lineItems}
                    transactionType={transactionType}
                  />
                )}
              </Grid>
              <Grid
                container
                spacing={2}
                direction="row"
                alignItems="center"
                marginBottom={"1.2em"}
              >
                <DocumentCurrencyInput
                  control={control}
                  errors={errors}
                  documentCurrencyCode={useWatch({
                    control,
                    name: "document_currency_code",
                  })}
                  readOnly={readOnly}
                  setValue={setValue}
                />
              </Grid>
            </Grid>
            <ItemDetailsTable
              items={lineItems}
              remove={removeLineItem}
              replace={replaceLineItems}
              add={appendLineItem}
              readOnly={readOnly}
              transactionType={transactionType}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid size={{ md: 6 }}>
            <Grid container spacing={1}>
              <Grid item md={12}>
                <DocumentLevelChargeAndAllowancesButton
                  control={control}
                  readOnly={readOnly}
                  currentNetAmount={totalExcludingTax}
                  itemBaseAmount={totalExcludingTax}
                />
              </Grid>
              <Grid size={{ md: 12 }}>
                {isPrepayment && (
                  <AddPrepaymentDetailsButton
                    control={control}
                    error={errors}
                    readOnly={readOnly}
                    currentNetAmount={totalIncludingTax}
                    addPrepaidAmount={addPrepaidAmount}
                    addPrepaymentDetails={addPrepaymentDetails}
                    getValues={getValues}
                  />
                )}
              </Grid>
              <Grid size={{ md: 12 }}>
                {isProfitMargin && (
                  <AddPrecedingInvoiceDetailsButton
                    control={control}
                    error={errors}
                    readOnly={readOnly}
                    currentNetAmount={totalIncludingTax}
                    addPrecedingInvoiceDetails={addPrecedingInvoiceDetails}
                    getValues={getValues}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>
          <Grid size={{ md: 6 }}>
            <DocumentSummarySection
              itemsSubtotal={itemsSubtotal}
              subtotal={subtotal}
              totalItemAllowances={totalItemAllowances}
              totalItemCharges={totalItemCharges}
              totalExcludingTax={totalExcludingTax}
              totalIncludingTax={totalIncludingTax}
              totalTaxAmount={totalTaxAmount}
              taxInOMR={taxInOMR}
              totalPayableAmount={totalPayableAmount}
              documentCurrencyCode={getValues("document_currency_code")}
              headerCharges={headerCharges}
              headerAllowance={headerAllowance}
              totalTaxableAmount={totalTaxableAmount}
              totalNonTaxableAmount={totalNonTaxableAmount}
              taxBreakdown={taxBreakdown}
              payableRoundingAmount={payableRoundingAmount}
              prepaidAmount={prepaidAmount}
              readOnly={readOnly}
              addPrepaidAmount={addPrepaidAmount}
              addPrepaymentDetails={addPrepaymentDetails}
              getValues={getValues}
            />
          </Grid>
        </Grid>
        <DocumentNoteField
          control={control}
          errors={errors}
          readOnly={readOnly}
        />
      </Box>
    </>
  );
}
